import { InitializationState } from './model/initializations-state.enum';
import { Tracking } from './model/tracking.interface';
import { IdentificationData } from './model/identification-data';

type PickFunctions<T> = {
  [K in keyof T]: Extract<T[K], (...args: any[]) => any>;
};

export class TrackingProvider implements Tracking {
  private initPromise?: Promise<unknown>;
  private state = InitializationState.PENDING;

  constructor(
    private readonly implementation: Tracking,
    private readonly initTimeoutMs = 5000
  ) {}

  get trackingState(): InitializationState {
    return this.state;
  }

  async initialize(identity?: IdentificationData): Promise<void> {
    await this.ensureInit(identity);
  }

  identify(identity: IdentificationData): void {
    void this.callImplementation('identify', identity);
  }

  setUserData(data: Record<string, any>): void {
    void this.callImplementation('setUserData', data);
  }

  trackEvent(name: string, props?: Record<string, any>): void {
    void this.callImplementation('trackEvent', name, props);
  }

  trackClick(selector: string, props?: Record<string, any>): void {
    void this.callImplementation('trackClick', selector, props);
  }

  trackNavigation(url: string, props?: Record<string, any>): void {
    void this.callImplementation('trackNavigation', url, props);
  }

  private async callImplementation<
    K extends keyof Required<PickFunctions<Tracking>>,
    M extends Tracking[K],
    N extends M extends (...args: any[]) => any ? M : never,
    Params extends Parameters<N>
  >(method: K, ...args: Params): Promise<void> {
    const callee = this.implementation[method] as (
      ...args: Params
    ) => ReturnType<N>;
    if (!callee) {
      console.warn(
        `[Tracking] Method "${method}" not implemented in tracking provider`
      );
      return;
    } else if (this.state === InitializationState.FAILED) {
      console.warn(
        `[Tracking] Action "${method}" skipped: initialization failed`
      );
      return;
    }

    try {
      await this.ensureInit();
      await callee.bind(this.implementation)(...args);
    } catch (e) {
      console.warn(`[Tracking] Failed to call tracking method "${method}":`, e);
      return;
    }
  }

  private async ensureInit(identity?: IdentificationData): Promise<void> {
    if (this.initPromise) {
      // Initialize just once
      await this.initPromise;
    }

    this.state = InitializationState.INITIALIZING;

    this.initPromise = Promise.race([
      this.implementation.initialize(identity),
      this.createTimeoutPromise(),
    ])
      .then(() => (this.state = InitializationState.INITIALIZED))
      .catch((err) => {
        this.state = InitializationState.FAILED;
        console.error('TrackingProvider initialization failed:', err);
        throw err;
      });

    await this.initPromise;
  }

  private createTimeoutPromise() {
    return new Promise<void>((_, reject) =>
      setTimeout(
        () =>
          reject(
            new Error(
              `Tracking initialization timed out after ${this.initTimeoutMs}ms`
            )
          ),
        this.initTimeoutMs
      )
    );
  }
}
